import { McButton, McOption, McSelect, McTable } from '@maersk-global/mds-react-wrapper';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import Drawer from '../../../common/components/Drawer/Drawer';
import { GateHoursDailyOverride } from '../../../common/service/gate-hours/types';
import RemarksRenderer from '../remarks-renderer';
import { MappedGateHours } from './gate-hours';
import { groupDailyOverridesByDate, parseHours } from './helpers';

const columns: TableColumn[] = [
  { id: 'date', label: 'Date', align: 'left', sortDisabled: true, width: '220px' },
  { id: 'hours', label: 'Hours', align: 'left', sortDisabled: true },
  { id: 'remarks', label: 'Remarks', align: 'left', sortDisabled: true },
  { id: 'actions', label: 'Actions', align: 'right', sortDisabled: true, width: '160px' },
];

type WeekRow = MappedGateHours & { dateLabel: string; overrideDate: string; isOverride: boolean };

type OverwriteWeekDrawerProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  data: MappedGateHours[];
  dailyOverrides: GateHoursDailyOverride[];
  onEditRow?: (row: WeekRow) => void;
};

const formatDateOnlyLabel = (date: Date) =>
  date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const formatIsoDateOnly = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatWeekRange = (start: Date, end: Date) =>
  `${start.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}-${end.toLocaleDateString('en-GB', {
    month: 'short',
    day: 'numeric',
  })}`;

const getMonday = (date: Date) => {
  const monday = new Date(date);
  const day = monday.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const addDays = (date: Date, amount: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const getIsoWeekNumber = (date: Date) => {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayOfWeek = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayOfWeek);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const daysSinceYearStart = (utcDate.getTime() - yearStart.getTime()) / 86400000 + 1;
  return Math.ceil(daysSinceYearStart / 7);
};

const OverwriteWeekDrawer = ({
  open,
  setOpen,
  onClose,
  onSave,
  data,
  dailyOverrides,
  onEditRow,
}: OverwriteWeekDrawerProps) => {
  const [selectedWeek, setSelectedWeek] = useState('week-1');
  const baseWeekStart = useMemo(() => getMonday(new Date()), []);
  const weekOptions = useMemo(
    () =>
      Array.from({ length: 8 }).map((_, index) => {
        const start = addDays(baseWeekStart, index * 7);
        const end = addDays(start, 6);
        return {
          value: `week-${index + 1}`,
          weekNumberLabel: `Week ${getIsoWeekNumber(start)}`,
          rangeLabel: formatWeekRange(start, end),
          start,
        };
      }),
    [baseWeekStart],
  );

  const selectedWeekStart = weekOptions.find((week) => week.value === selectedWeek)?.start || weekOptions[0].start;
  const overrideByDate = useMemo(() => groupDailyOverridesByDate(dailyOverrides), [dailyOverrides]);

  const rows: WeekRow[] = useMemo(
    () =>
      data.map((row, index) => {
        const rowDate = addDays(selectedWeekStart, index);
        const overrideDate = formatIsoDateOnly(rowDate);
        const override = overrideByDate[overrideDate];

        if (!override) {
          return {
            ...row,
            dateLabel: formatDateOnlyLabel(rowDate),
            overrideDate,
            isOverride: false,
          };
        }

        const overrideHours = override.fullDayOpen
          ? 'All day'
          : override.timeSlots.length
            ? parseHours(override.timeSlots)
            : 'Closed';

        return {
          ...row,
          remarks: override.remarks ? `Overwrite\n${override.remarks}` : 'Overwrite',
          fullDayOpen: override.fullDayOpen,
          isOpen: override.fullDayOpen || override.timeSlots.length > 0,
          hours: overrideHours,
          dateLabel: formatDateOnlyLabel(rowDate),
          overrideDate,
          isOverride: true,
        };
      }),
    [data, selectedWeekStart, overrideByDate],
  );

  const overrideRowStyles = useMemo(() => {
    const overrideRowIds = rows.filter((row) => row.isOverride).map((row) => row.id);

    if (!overrideRowIds.length) {
      return '';
    }

    const baseSelectors = overrideRowIds.map((rowId) => `.row-${rowId} td`).join(', ');
    const hoverSelectors = overrideRowIds.map((rowId) => `.row-${rowId}:hover td`).join(', ');

    return `${baseSelectors}, ${hoverSelectors} { background-color: #edf6ff !important; }`;
  }, [rows]);

  return (
    <Drawer title="Gate hours" open={open} setOpen={setOpen} onRequestClose={onClose} noFooter wider>
      <div className="HolidaysDrawer__form">
        <div className="HolidaysDrawer__content OverwriteWeekDrawer">
          <div className="OverwriteWeekDrawer__week-select-wrapper">
            <div className="OverwriteWeekDrawer__week-label">Week</div>
            <McSelect
              hiddenlabel
              value={selectedWeek}
              optionselected={(event) => {
                setSelectedWeek((event.target as HTMLInputElement).value);
              }}
              name="week"
            >
              {weekOptions.map((week) => (
                <McOption key={week.value} value={week.value} label="">
                  <span>
                    <strong>{week.weekNumberLabel} </strong>
                  </span>
                  <span> - {week.rangeLabel}</span>
                </McOption>
              ))}
            </McSelect>
          </div>

          <McTable columns={columns} datakey="id" data={rows} customstyles={overrideRowStyles}>
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <div slot={`${row.id}_date`}>
                  <span className="OverwriteWeekDrawer__day-cell">
                    <span className="OverwriteWeekDrawer__day-name">{row.day}</span>
                    <span>{row.dateLabel}</span>
                  </span>
                </div>
                <div slot={`${row.id}_remarks`}>
                  <RemarksRenderer remarks={row.remarks} />
                </div>
                <div slot={`${row.id}_actions`}>
                  <McButton
                    fit="small"
                    variant="plain"
                    appearance="primary"
                    icon="pencil"
                    click={() => onEditRow?.(row)}
                  >
                    Edit
                  </McButton>
                </div>
              </React.Fragment>
            ))}
          </McTable>
        </div>
      </div>
    </Drawer>
  );
};

export default OverwriteWeekDrawer;
